import React, { useEffect, useState } from 'react';
import { Button, Tooltip } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import moment from 'moment';

import DateTimeRangePicker, { DateTimeRange } from './DateTimeRangePicker';

const LightTooltip = withStyles(() => ({
  tooltip: {
    backgroundColor: 'white',
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: '0px 1px 3px 0px rgba(0,0,0,0.4)',
    fontSize: 11,
  },
}))(Tooltip);

interface TooltipDateTimeRangePickerProps {
  updateDateTimeRange?(dateTimeRange: DateTimeRange): void;
  setUpdated?(updated: boolean): void;
  initialDateTimeRange?: DateTimeRange;
  isActive?: boolean;
  CustomComponent: React.ComponentType;
}

const defaultDateTimeRange: DateTimeRange = {
  start: moment().startOf('minute').subtract(1, 'h'),
  end: moment().startOf('minute'),
};

const TooltipDateTimeRangePicker: React.FC<TooltipDateTimeRangePickerProps> = ({
  updateDateTimeRange = () => {},
  setUpdated = () => {},
  initialDateTimeRange = defaultDateTimeRange,
  isActive = false,
  CustomComponent,
}: TooltipDateTimeRangePickerProps) => {
  const [dateTimeTooltipOpen, setDateTimeTooltipOpen] = useState(false);
  const [dateTimeRange, setDateTimeRange] = useState(initialDateTimeRange);
  const [customDatePickerState, setCustomDatePickerState] = useState<
    Pick<React.ComponentProps<typeof Button>, 'color' | 'variant'>
  >({
    color: isActive ? 'primary' : 'default',
    variant: isActive ? 'contained' : 'outlined',
  });

  useEffect(() => {
    setDateTimeRange(initialDateTimeRange);
  }, [initialDateTimeRange]);

  useEffect(() => {
    setCustomDatePickerState({
      color: isActive ? 'primary' : 'default',
      variant: isActive ? 'contained' : 'outlined',
    });
  }, [isActive]);

  return (
    <LightTooltip
      PopperProps={{
        disablePortal: true,
      }}
      onClose={() => {
        setDateTimeTooltipOpen(false);
      }}
      open={dateTimeTooltipOpen}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      title={
        <DateTimeRangePicker
          startDateTime={dateTimeRange.start}
          endDateTime={dateTimeRange.end}
          setDateTimeRange={(newDatetimeRange) => {
            setDateTimeRange(newDatetimeRange);
            updateDateTimeRange(newDatetimeRange);
          }}
          setUpdated={setUpdated}
          setTooltipOpen={setDateTimeTooltipOpen}
        />
      }
      interactive
    >
      <Button
        color={customDatePickerState.color}
        variant={customDatePickerState.variant}
        onClick={() => {
          setDateTimeTooltipOpen(!dateTimeTooltipOpen);
        }}
      >
        <CustomComponent />
      </Button>
    </LightTooltip>
  );
};

export default TooltipDateTimeRangePicker;
